import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Save, IndianRupee, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const [rate, setRate] = useState('');
  const [showDebit, setShowDebit] = useState(false);
  const [info, setInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/settings`, { headers: { 'Authorization': `Bearer ${token}` } });
      const d = await r.json();
      setInfo(d || {});
      setRate(String(d.rate_per_km ?? 2.5));
      setShowDebit(!!d.show_unplan_debit_to_staff);
    } finally { setLoading(false); }
  };

  const saveField = async (payload, message) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        setSavedAt(message + ' · ' + new Date().toLocaleTimeString());
        fetchSettings();
      } else {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || 'Save failed');
      }
    } finally { setSaving(false); }
  };

  const saveRate = async (e) => {
    e?.preventDefault();
    const value = parseFloat(rate);
    if (isNaN(value) || value < 0) { alert('Enter a valid rate'); return; }
    if (!window.confirm(`Set master rate to ₹${value.toFixed(2)} per km?`)) return;
    saveField({ rate_per_km: value }, 'Rate saved');
  };

  const toggleShowDebit = async () => {
    const next = !showDebit;
    setShowDebit(next);
    saveField({ show_unplan_debit_to_staff: next }, next ? 'Debit visibility enabled' : 'Debit visibility disabled');
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div data-testid="settings-page">
      <div className="flex items-center space-x-2 mb-6">
        <SettingsIcon className="text-gray-700" size={24} />
        <h1 className="text-2xl font-bold text-gray-900">Master Settings</h1>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border max-w-xl p-6 mb-4">
        <form onSubmit={saveRate} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Rate per Kilometre (₹)
            </label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                data-testid="rate-per-km-input"
                type="number" step="0.01" min="0"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl text-2xl font-bold focus:border-blue-400 outline-none"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Applied to <strong>own-vehicle</strong> visits at end-trip.
              A staff member's individual rate (if set on their profile) overrides this master rate.
            </p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            <Save size={18} /><span>{saving ? 'Saving...' : 'Save Master Rate'}</span>
          </button>
        </form>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border max-w-xl p-6 mb-4" data-testid="unplan-debit-card">
        <h2 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2">
          {showDebit ? <Eye size={18} className="text-emerald-600" /> : <EyeOff size={18} className="text-gray-400" />}
          Show Unplan-Visit debit to staff
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          When ON, every time you debit hours for an unplan visit, the staff gets a WhatsApp ping
          and the deduction shows up in their Report tab. When OFF (default), debit stays as a private admin record.
        </p>
        <button
          onClick={toggleShowDebit}
          disabled={saving}
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
            showDebit ? 'bg-emerald-600' : 'bg-gray-300'
          } disabled:opacity-50`}
          data-testid="toggle-show-debit"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              showDebit ? 'translate-x-8' : 'translate-x-1'
            }`}
          />
        </button>
        <span className="ml-3 text-sm font-medium text-gray-700">{showDebit ? 'ON — visible to staff' : 'OFF — private to admin'}</span>
      </div>

      {savedAt && (
        <div className="max-w-xl text-xs text-green-700 bg-green-50 border border-green-200 p-3 rounded-lg" data-testid="settings-saved">
          ✓ {savedAt}
        </div>
      )}

      {info.updated_at && (
        <p className="text-xs text-gray-500 mt-2 max-w-xl">
          Last updated: {new Date(info.updated_at).toLocaleString('en-IN')}
        </p>
      )}
    </div>
  );
}
